import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { CompetencySuggestion, EditableCompetencyFields } from "../types";

import { getInterviewerNotesKey } from "../store/helpers";

import { competenciesKeys } from "../../../api/hooks/defs/competencies";
import { competencyAssignmentsKeys } from "../../../api/hooks/defs/competency-assignments";

import internalRequest, { ErrorResponse } from "../../../api/services/defs/internal-api/internal-base-request";

import { useAskPerformYardStore } from "../AskPerformYardStore";

type ResolvedCompetency = {
	name: string;
	description: string;
	levels: Array<{ rank: number; name: string; description: string }>;
	is_company_wide: boolean;
};

const ALL_REMOVED_NUDGE =
	"It looks like none of those competencies were a fit — tell me what you're looking for and I'll put together a different set.";

const finalizeCompetencyFramework = () => {
	const state = useAskPerformYardStore.getState();
	const harness = state.activeInterviewerHarness;
	if (harness) {
		sessionStorage.removeItem(getInterviewerNotesKey(harness));
	}
	state.deactivateInterviewerHarness();
	state.setCompetencyFrameworkStatus("saved");
};

// Record a fact in the active interviewer's note-taker notes so it survives the
// round-trip to the agent. Notes conform to the harness note schema; extraContext
// is the free-form bucket the note-taker preserves across turns.
const appendInterviewerNote = (note: string) => {
	const harness = useAskPerformYardStore.getState().activeInterviewerHarness;
	if (!harness) return;
	const key = getInterviewerNotesKey(harness);
	try {
		const raw = sessionStorage.getItem(key);
		const notes = raw ? JSON.parse(raw) : {};
		const extraContext = Array.isArray(notes.extraContext) ? notes.extraContext : [];
		notes.extraContext = [...extraContext, note];
		sessionStorage.setItem(key, JSON.stringify(notes));
	} catch {
		// sessionStorage read/write failures are non-fatal
	}
};

// Drive the framework to its terminal state after a per-item create or remove.
// Once every suggestion is created-or-removed: if at least one was created, finalize
// (deactivate harness, clear notes, mark saved); if the user removed all of them
// without creating any, keep the interviewer harness active so the conversation
// continues, note the rejection for the agent, and nudge for a new direction.
const resolveTerminalCompetencyFramework = () => {
	const framework = useAskPerformYardStore.getState().competencyFramework;
	if (!framework) return;
	const statusOf = (id: string) => framework.itemStatuses[id] ?? "pending";
	const terminal = framework.suggestions.every(
		(item) => statusOf(item.id) === "created" || statusOf(item.id) === "removed"
	);
	if (!terminal) return;
	const anyCreated = framework.suggestions.some((item) => statusOf(item.id) === "created");
	if (anyCreated) {
		finalizeCompetencyFramework();
		return;
	}
	appendInterviewerNote(
		"The user removed every suggested competency without creating any. Do not re-suggest the removed competencies; ask what they want instead before proposing a new framework."
	);
	useAskPerformYardStore.getState().addStaticAssistantMessage(ALL_REMOVED_NUDGE);
};

export const useCompetencyCreation = () => {
	const isSaving = useAskPerformYardStore((store) => store.competencyFramework?.status === "saving");
	const setCompetencyFrameworkStatus = useAskPerformYardStore((store) => store.setCompetencyFrameworkStatus);
	const addStaticAssistantMessage = useAskPerformYardStore((store) => store.addStaticAssistantMessage);
	const queryClient = useQueryClient();

	const invalidateCompetencyLists = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: competenciesKeys.all });
		queryClient.invalidateQueries({ queryKey: competencyAssignmentsKeys.all });
	}, [queryClient]);

	const saveFramework = useCallback(
		async (resolvedCompetencies: ResolvedCompetency[], savedIds: string[] = []) => {
			if (useAskPerformYardStore.getState().competencyFramework?.status === "saving") return;
			setCompetencyFrameworkStatus("saving");
			try {
				await internalRequest("/v1/competencies/bulk", {
					method: "POST",
					body: JSON.stringify({ competencies: resolvedCompetencies }),
				});
				invalidateCompetencyLists();
				savedIds.forEach((id) => {
					useAskPerformYardStore.getState().markCompetencyCreated(id);
				});
				finalizeCompetencyFramework();
			} catch (error) {
				if (error instanceof ErrorResponse && error.response.status === 409) {
					const errorMessage =
						(error.data as { message?: string })?.message ||
						"Failed to save competencies. Please try again.";
					addStaticAssistantMessage(
						`${errorMessage}. To fix this, either click **Edit** on the framework card to rename them yourself, or reply here with your preferred names and I'll update the framework.`
					);
					setCompetencyFrameworkStatus("pending");
					return;
				}
				const errorMessage =
					error instanceof ErrorResponse
						? ((error.data as { message?: string })?.message ?? error.message)
						: error instanceof Error
							? error.message
							: "Failed to save competencies. Please try again.";
				setCompetencyFrameworkStatus("error", errorMessage || "Failed to save competencies. Please try again.");
			}
		},
		[addStaticAssistantMessage, setCompetencyFrameworkStatus, invalidateCompetencyLists]
	);
	const createOne = useCallback(
		async (suggestion: CompetencySuggestion): Promise<boolean> => {
			const store = useAskPerformYardStore.getState();
			const framework = store.competencyFramework;
			if (!framework) return false;
			if (framework.status === "saving") return false;
			if (framework.itemStatuses[suggestion.id] === "created") return true;
			if (framework.itemStatuses[suggestion.id] === "creating") return false;

			store.markCompetencyCreating(suggestion.id);
			const [resolved] = resolveCompetencies([suggestion], framework.editedFields);
			try {
				await internalRequest("/v1/competencies/bulk", {
					method: "POST",
					body: JSON.stringify({ competencies: [resolved] }),
				});
				invalidateCompetencyLists();
				useAskPerformYardStore.getState().markCompetencyCreated(suggestion.id);
				resolveTerminalCompetencyFramework();
				return true;
			} catch (error) {
				const message =
					error instanceof ErrorResponse
						? ((error.data as { message?: string })?.message ?? error.message)
						: "Failed to create competency. Please try again.";
				useAskPerformYardStore.getState().setCompetencyItemError(suggestion.id, message);
				return false;
			}
		},
		[invalidateCompetencyLists]
	);

	const removeOne = useCallback((id: string) => {
		useAskPerformYardStore.getState().removeCompetency(id);
		resolveTerminalCompetencyFramework();
	}, []);

	return { saveFramework, createOne, removeOne, isSaving };
};

export const resolveCompetencies = (
	suggestions: CompetencySuggestion[],
	editedFields: Record<string, Partial<EditableCompetencyFields>>
) =>
	suggestions.map((suggestion) => ({
		name: editedFields[suggestion.id]?.name ?? suggestion.name,
		description: editedFields[suggestion.id]?.description ?? suggestion.description,
		levels: editedFields[suggestion.id]?.levels ?? suggestion.levels,
		is_company_wide: editedFields[suggestion.id]?.isCompanyWide ?? suggestion.isCompanyWide ?? false,
	}));
